import { capitalizeFirstLetter } from '@/pages/agents/utils';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { InsightsSentiment, ProcessedInsightsUserTopicsItem } from '../../utils/processInsightsUserTopics';
import { MetricTile } from '../MetricTile';
import { InsightsColumn, TableRow } from '../Table';
import SadCircleIcon from './icons/SadCircle.svg';
import SmileCircleIcon from './icons/SmileCircle.svg';

const Cell = styled.td<{ grow?: number; minWidth?: number }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-align: left;
    color: ${theme.colors.black[700]};
    font-size: 14px;
    font-weight: 400;
    padding: 0;

    ${({ grow }) => grow && `flex: ${grow};`}
    ${({ minWidth }) => minWidth && `min-width: ${minWidth}px;`}
`;

const TopicPile = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    color: ${theme.colors.secondary[900]};
    font-size: 14px;
    font-weight: 400;

    border-radius: 90px;
    border: 1px solid ${theme.colors.black[300]};
    background: ${theme.colors.white};
    padding: 4px 8px;
`;

const IntentText = styled.span`
    max-width: 240px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    overflow: hidden;
    text-overflow: ellipsis;

    :hover {
        text-decoration: underline;
    }
`;

interface Props {
    columns: InsightsColumn[];
    item: ProcessedInsightsUserTopicsItem;
    onClick?: () => void;
}

const TopicCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        <TopicPile>{capitalizeFirstLetter(item.topic)}</TopicPile>
    </Cell>
);

const IntentCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        <Tooltip
            title={item.intent}
            placement="top"
            slotProps={{
                tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } },
            }}>
            <IntentText>{capitalizeFirstLetter(item.intent)}</IntentText>
        </Tooltip>
    </Cell>
);

const SatisfactionCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        <MetricTile
            metrics={[
                {
                    value: `${item.positive_conversation_sentiment_percentage}%`,
                    sentiment: 'positive' as const,
                    icon: SmileCircleIcon,
                },
                {
                    value: `${item.negative_conversation_sentiment_percentage}%`,
                    sentiment: 'negative' as const,
                    icon: SadCircleIcon,
                },
            ]}
            size="default"
        />
    </Cell>
);

const UsersCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        {item.users}
        {!!item.users_change && (
            <MetricTile metrics={[{ value: `${item.users_change}%`, sentiment: item.users_sentiment as InsightsSentiment }]} size="small" />
        )}
    </Cell>
);

const ConversationsCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        {item.n_sessions}
        {!!item.sessions_change && (
            <MetricTile
                metrics={[{ value: `${item.sessions_change}%`, sentiment: item.sessions_sentiment as InsightsSentiment }]}
                size="small"
            />
        )}
    </Cell>
);

const TotalCell = ({ column, item }: { column: InsightsColumn; item: ProcessedInsightsUserTopicsItem }) => (
    <Cell minWidth={column.minWidth}>{item.total_interactions}%</Cell>
);

export const TopicRow = ({ columns, item, onClick }: Props) => {
    return (
        <TableRow onClick={onClick}>
            {columns.map(column => {
                switch (column.label) {
                    case 'Topic':
                        return <TopicCell key={column.label} column={column} item={item} />;
                    case 'Intent':
                        return <IntentCell key={column.label} column={column} item={item} />;
                    case 'Satisfaction':
                        return <SatisfactionCell key={column.label} column={column} item={item} />;
                    case 'Users':
                        return <UsersCell key={column.label} column={column} item={item} />;
                    case 'Conversations':
                        return <ConversationsCell key={column.label} column={column} item={item} />;
                    case 'Total':
                        return <TotalCell key={column.label} column={column} item={item} />;
                    default:
                        return null;
                }
            })}
        </TableRow>
    );
};
